/**
 * <cart-panel-subdrawer>
 *
 * Generic primitive: one element per sub-drawer (note / discount / share).
 * Public methods: open(), close(). Closes peers on open via a custom event.
 *
 * <cart-panel-discount> and <cart-panel-share> are siblings in this file —
 * they're tied together by behaviour (both live in sub-drawers) but each
 * owns its own logic.
 */

class CartPanelSubdrawer extends HTMLElement {
  constructor() {
    super();

    // Listeners on `this` auto-clean when the element is GC'd. The peer-close
    // listener on `document` does NOT — it lives until removeEventListener.
    // Move it to connectedCallback / disconnectedCallback so the body
    // innerHTML swap on empty<->non-empty cart transitions doesn't leave
    // zombie closures on document each time (3 subdrawers × N transitions).
    this.addEventListener('keydown', (event) => {
      if (event.code === 'Escape' && this.classList.contains('is-open')) {
        event.stopPropagation();
        this.close();
      }
    });
  }

  connectedCallback() {
    if (this._peerCloseListener) return;
    this._peerCloseListener = (event) => {
      if (event && event.detail && event.detail.id !== this.dataset.subdrawer) {
        this.close();
      }
    };
    document.addEventListener('cart-panel:subdrawer:open', this._peerCloseListener);
  }

  disconnectedCallback() {
    if (this._peerCloseListener) {
      document.removeEventListener('cart-panel:subdrawer:open', this._peerCloseListener);
      this._peerCloseListener = undefined;
    }
  }

  open() {
    this.classList.add('is-open');
    document.dispatchEvent(
      new CustomEvent('cart-panel:subdrawer:open', {
        detail: { id: this.dataset.subdrawer },
      })
    );
    // Move focus to the first interactive element inside, BUT prevent the
    // browser's "scroll element into view" — otherwise it scrolls the items
    // region (which is overflow:auto) and the cart pane jumps.
    const focusable = this.querySelector(
      'input, textarea, button:not([data-subdrawer-close])'
    );
    if (focusable) {
      requestAnimationFrame(() => {
        try { focusable.focus({ preventScroll: true }); } catch (_) { /* ignore */ }
      });
    }
  }

  close() {
    this.classList.remove('is-open');
  }
}

customElements.define('cart-panel-subdrawer', CartPanelSubdrawer);

/* ------------------------------------------------------------------ */

/**
 * <cart-panel-discount>
 *
 * Apply / remove discount codes via /cart/update.js with a comma-joined
 * `discount` field (canonical Shopify multi-code pattern). The response's
 * discount_codes[].applicable list tells us which codes stuck.
 */
class CartPanelDiscount extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="text"]');
    this.statusEl = this.querySelector('[data-discount-status]');
    this.applyBtn = this.querySelector('[data-discount-apply]');

    this.freeShippingCodes = (this.dataset.freeShippingCodes || '')
      .split(',')
      .map((c) => c.trim().toLowerCase())
      .filter(Boolean);

    if (this.applyBtn) {
      this.applyBtn.addEventListener('click', () => this.apply());
    }
    if (this.input) {
      this.input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          this.apply();
        }
      });
    }
    this.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-discount-remove]');
      if (btn) {
        e.preventDefault();
        if (btn.disabled) return;
        this.removeCode(btn.dataset.discountRemove, btn);
      }
    });
  }

  appliedCodes() {
    return Array.from(this.querySelectorAll('[data-discount-remove]')).map(
      (el) => el.dataset.discountRemove
    );
  }

  async apply() {
    const code = (this.input.value || '').trim();
    if (!code) return;

    if (this.freeShippingCodes.includes(code.toLowerCase())) {
      return this._showStatus('freeShipping');
    }

    if (this.appliedCodes().some((c) => c.toLowerCase() === code.toLowerCase())) {
      this.input.value = '';
      return this._showStatus('alreadyApplied');
    }

    const next = [...this.appliedCodes(), code].join(',');
    this._setLoading(true);
    let succeeded = false;
    try {
      const res = await this._postDiscount(next);
      const applicable = (res?.discount_codes || []).filter((c) => c.applicable).map((c) => c.code);
      if (applicable.some((c) => c.toLowerCase() === code.toLowerCase())) {
        this.input.value = '';
        this._showStatus('success');
        succeeded = true;
      } else {
        this._showStatus('invalid');
      }
      this._refreshHost(res);
    } catch (e) {
      // Network failure / non-JSON. The status row stays visible after
      // _showStatus's TTL elapses, so use the same channel for parity.
      console.error('cart-panel-discount: apply failed', e);
      this._showStatus('invalid');
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS === 'object') {
        publish(PUB_SUB_EVENTS.cartError, {
          source: 'cart-panel-discount',
          message: (e && e.message) || 'Network error',
        });
      }
    } finally {
      this._setLoading(false);
      if (succeeded && this.applyBtn) {
        this._flashSuccess(this.applyBtn);
      }
    }
  }

  /**
   * Briefly flash the success tick on the button (label hidden, checkmark
   * shown) before reverting to the original Apply label. Disable the
   * button during the flash so a quick double-click can't re-trigger
   * apply mid-animation.
   */
  _flashSuccess(btn) {
    btn.classList.add('is-success');
    btn.disabled = true;
    setTimeout(() => {
      btn.classList.remove('is-success');
      btn.disabled = false;
    }, 1100);
  }

  async removeCode(code, triggerBtn) {
    const next = this.appliedCodes()
      .filter((c) => c !== code)
      .join(',');
    // Loading state on the specific chip's trash + freeze sibling chips
    // so the user can't double-click during the in-flight fetch. The
    // button itself gets disposed of when the chip list is swapped from
    // parsedState, so there's no need to clear the loading class — the
    // node is just gone.
    if (triggerBtn) {
      triggerBtn.classList.add('loading');
      triggerBtn.disabled = true;
    }
    this.querySelectorAll('[data-discount-remove]').forEach((b) => {
      b.disabled = true;
    });
    try {
      const res = await this._postDiscount(next);
      this._refreshHost(res);
    } catch (e) {
      console.error('cart-panel-discount: remove failed', e);
      // On failure, restore interaction so the user can retry, surface the
      // status, and publish cartError so other subscribers can react.
      this.querySelectorAll('[data-discount-remove]').forEach((b) => {
        b.disabled = false;
        b.classList.remove('loading');
      });
      this._showStatus('invalid');
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS === 'object') {
        publish(PUB_SUB_EVENTS.cartError, {
          source: 'cart-panel-discount',
          message: (e && e.message) || 'Network error',
        });
      }
    }
  }

  _postDiscount(commaJoined) {
    return fetch(`${routes.cart_update_url}`, {
      ...fetchConfig(),
      body: JSON.stringify({
        discount: commaJoined,
        sections: 'cart-panel,cart-icon-bubble',
        sections_url: window.location.pathname,
      }),
    }).then((r) => r.json());
  }

  /**
   * Swap the applied-codes block (inside our subdrawer) and hand the rest of
   * the panel re-render off to renderContents — which uses the same parsed
   * sections the discount POST already returned. Previously this called
   * refreshSections, paying for a redundant /cart?section_id=cart-panel
   * + /cart?section_id=cart-icon-bubble round-trip when we already had both
   * payloads from the apply/remove response.
   *
   * The applied-codes block isn't covered by renderContents (it lives inside
   * the open subdrawer, which we deliberately preserve), so it stays as a
   * targeted swap here.
   */
  _refreshHost(parsedState) {
    const sectionHtml = parsedState && parsedState.sections && parsedState.sections['cart-panel'];
    if (sectionHtml) {
      const newDoc = new DOMParser().parseFromString(sectionHtml, 'text/html');

      // Applied-codes block inside our own subdrawer — renderContents won't
      // touch this (it skips the .cart-panel__interact-region siblings).
      const newApplied = newDoc.querySelector('cart-panel-discount .cart-panel__discount-applied');
      const curApplied = this.querySelector('.cart-panel__discount-applied');
      if (newApplied && curApplied) {
        curApplied.replaceWith(newApplied);
      } else if (newApplied && !curApplied) {
        this.appendChild(newApplied);
      } else if (!newApplied && curApplied) {
        curApplied.remove();
      }
    }

    const panel = this.closest('cart-panel');
    if (panel && typeof panel.renderContents === 'function') {
      panel.renderContents(parsedState);
    }
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS === 'object') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-panel', cartData: parsedState });
    }
  }

  _setLoading(on) {
    if (this.applyBtn) {
      // `.loading` (not `.is-loading`) so the shared `.button.loading {
      // color: transparent }` rule from base.css applies and the
      // `.cart-panel__subdrawer-cta-spinner` swap kicks in via the
      // sibling rules in component-cart-panel.css.
      this.applyBtn.classList.toggle('loading', !!on);
      this.applyBtn.disabled = !!on;
    }
  }

  _showStatus(kind) {
    if (!this.statusEl) return;
    const map = {
      success: { state: 'success', key: 'discountApplied', ttl: 3000 },
      invalid: { state: 'error', key: 'discountInvalid', ttl: 5000 },
      freeShipping: { state: 'error', key: 'discountFreeShipping', ttl: 5000 },
      alreadyApplied: { state: 'info', key: 'discountAlreadyApplied', ttl: 3000 },
    };
    const m = map[kind] || map.invalid;
    this.statusEl.dataset.state = m.state;
    this.statusEl.textContent = (window.cartStrings && window.cartStrings[m.key]) || '';
    this.statusEl.hidden = false;
    clearTimeout(this._statusTimer);
    this._statusTimer = setTimeout(() => {
      this.statusEl.hidden = true;
    }, m.ttl);
  }
}

customElements.define('cart-panel-discount', CartPanelDiscount);

/* ------------------------------------------------------------------ */

/**
 * <cart-panel-share>
 *
 * Builds the share URL and copies it to the clipboard. URL format mirrors
 * eurus's share-cart.js convention so the same URLs round-trip:
 *   `${origin}?id:N,q:M&id:...&share_cart:true`
 */
class CartPanelShare extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[readonly]');
    this.copyBtn = this.querySelector('[data-share-copy]');
    if (this.copyBtn) {
      this.copyBtn.addEventListener('click', () => this.copy());
    }
  }

  connectedCallback() {
    if (this._wired) return;
    this._wired = true;
    this.generate();

    // Subscribe to cart updates as a best-effort live refresh, but only fire
    // /cart.js when the share sub-drawer is actually open. Without this guard
    // every cart mutation (qty +/-, rec add, discount apply, note save)
    // would pay for a /cart.js round-trip just to keep an unviewed input
    // value fresh. The open-time and copy-time regenerates already cover
    // correctness, so this subscriber is purely about live-refreshing while
    // the user is looking at the URL.
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS === 'object') {
      this._unsub = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        const sd = this.closest('cart-panel-subdrawer');
        if (sd && sd.classList.contains('is-open')) {
          this.generate();
        }
      });
    }

    // Regenerate the URL whenever the share sub-drawer opens — guarantees the
    // displayed URL reflects the live cart state at view time, regardless of
    // whether the cartUpdate publish ever reached us.
    this._onSubdrawerOpen = (event) => {
      if (event && event.detail && event.detail.id === 'share') {
        this.generate();
      }
    };
    document.addEventListener('cart-panel:subdrawer:open', this._onSubdrawerOpen);
  }

  disconnectedCallback() {
    if (this._unsub) {
      this._unsub();
      this._unsub = undefined;
    }
    if (this._onSubdrawerOpen) {
      document.removeEventListener('cart-panel:subdrawer:open', this._onSubdrawerOpen);
      this._onSubdrawerOpen = undefined;
    }
    this._wired = false;
  }

  async generate() {
    try {
      const cartJsUrl = `${routes.cart_url}.js`.replace('//', '/');
      const res = await fetch(cartJsUrl, {
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      });
      const cart = await res.json();
      if (!cart || !Array.isArray(cart.items)) return;
      const params =
        cart.items
          .slice()
          .reverse()
          .map((it) => `id:${it.variant_id},q:${it.quantity}`)
          .join('&') + '&share_cart:true';
      if (this.input) {
        this.input.value = `${window.location.origin}?${params}`;
      }
      return this.input ? this.input.value : null;
    } catch (e) {
      console.error('cart-panel-share: generate failed', e);
    }
  }

  async copy() {
    if (!this.input || !navigator.clipboard) return;
    // Refresh right before copy so the clipboard always carries the live cart
    // state, even if the displayed value happens to be stale.
    await this.generate();
    navigator.clipboard
      .writeText(this.input.value)
      .then(() => {
        this.classList.add('is-copied');
        clearTimeout(this._copyTimer);
        this._copyTimer = setTimeout(() => {
          this.classList.remove('is-copied');
        }, 2000);
      })
      .catch((e) => console.error('cart-panel-share: copy failed', e));
  }
}

customElements.define('cart-panel-share', CartPanelShare);

/* ------------------------------------------------------------------ */

/**
 * <cart-panel-recommendations>
 *
 * Manages the mobile collapse / desktop always-expanded behaviour for the
 * recommendations pane. State persisted in localStorage.
 */
class CartPanelRecommendations extends HTMLElement {
  constructor() {
    super();
    this.toggleBtn = this.querySelector('.cart-panel__recs-toggle');
    if (this.toggleBtn) {
      this.toggleBtn.addEventListener('click', () => this._toggle());
    }
  }

  // window listeners must be attached/removed in connect/disconnect — the recs
  // region gets replaced via renderContents on every cart mutation, so a
  // constructor-attached resize listener would accumulate on window.
  connectedCallback() {
    if (!this._resyncListener) {
      this._resyncListener = this._sync.bind(this);
      window.addEventListener('resize', this._resyncListener);
    }
    this._sync();
  }

  disconnectedCallback() {
    if (this._resyncListener) {
      window.removeEventListener('resize', this._resyncListener);
      this._resyncListener = undefined;
    }
  }

  _isDesktop() {
    return window.matchMedia('(min-width: 990px)').matches;
  }

  _toggle() {
    if (this._isDesktop()) return; // always expanded on desktop
    const expanded = this.toggleBtn.getAttribute('aria-expanded') !== 'false';
    const next = !expanded;
    this.toggleBtn.setAttribute('aria-expanded', String(next));
    this.classList.toggle('is-collapsed', !next);
    try {
      localStorage.setItem('cart-panel:recs-expanded', JSON.stringify(next));
    } catch (_) { /* ignore */ }
  }

  _sync() {
    if (this._isDesktop()) {
      // Always expanded on desktop
      this.classList.remove('is-collapsed');
      this.toggleBtn?.setAttribute('aria-expanded', 'true');
      return;
    }
    let stored;
    try {
      stored = JSON.parse(localStorage.getItem('cart-panel:recs-expanded'));
    } catch (_) {
      stored = null;
    }
    const expanded = stored === null ? true : !!stored;
    this.classList.toggle('is-collapsed', !expanded);
    this.toggleBtn?.setAttribute('aria-expanded', String(expanded));
  }
}

customElements.define('cart-panel-recommendations', CartPanelRecommendations);
